import { Link, useParams } from "react-router-dom";
import { formatDate, formatPlayers, getCategoryByKey, getGameById, getRelatedGames } from "@/utils/gameUtils";
import { useSeo } from "@/hooks/useSeo";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import GameCard from "@/components/feature/GameCard";
import ShareBar from "@/components/feature/ShareBar";
import SectionHeader from "@/components/feature/SectionHeader";
import { usePlay } from "@/components/feature/PlayProvider";

export default function Detail() {
  const { gameId = "" } = useParams();
  const game = getGameById(gameId);
  const { openPlay } = usePlay();

  const category = game ? getCategoryByKey(game.category) : undefined;
  const relatedGames = game ? getRelatedGames(game, 4) : [];

  useSeo({
    title: game ? `【${game.name}】介绍与在线试玩 - 网页游戏直接玩` : "游戏详情 - 网页游戏直接玩",
    description: game
      ? `${game.name}：${game.summary}核心玩法与特色亮点一览，支持免下载在线试玩，${game.theme}题材${category?.name ?? "网页游戏"}。`
      : "查看网页游戏详情与在线试玩。",
    keywords: game
      ? `${game.name},${game.name}试玩,${category?.name ?? "网页游戏"},${game.tags.join(",")},网页游戏直接玩`
      : "网页游戏详情,在线试玩",
  });

  if (!game) {
    return (
      <div className="mx-auto max-w-screen2xl px-4 py-16 md:px-6">
        <EmptyState
          icon="ri-emotion-sad-line"
          title="游戏不存在或已下架"
          description="你访问的游戏可能已经下架或链接有误，回到首页继续探索其他热门游戏吧。"
          action={
            <Link
              to="/"
              className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-bold text-background-50 transition hover:bg-primary-600"
            >
              <i className="ri-home-4-line"></i>
              返回首页
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-screen2xl px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "游戏分类", to: `/category/${game.category}` },
          { label: category?.name ?? "游戏", to: `/category/${game.category}` },
          { label: game.name },
        ]}
      />

      <section className="relative mt-5 overflow-hidden rounded-lg">
        <img
          src={game.screenshots[0]}
          alt={`${game.name} 游戏主图`}
          title={`${game.name} 网页游戏在线试玩`}
          className="h-[300px] w-full object-cover object-top md:h-[400px]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/60 to-foreground-950/20" />
        <div className="absolute inset-0 flex flex-col justify-end p-5 md:p-8">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/category/${game.category}`}
              className="rounded-full bg-primary-500 px-3 py-1 text-xs font-bold text-background-50 transition hover:bg-primary-600"
            >
              {category?.name}
            </Link>
            <span className="rounded-full bg-background-50/15 px-3 py-1 text-xs font-medium text-background-50 backdrop-blur">
              {game.theme}
            </span>
          </div>
          <h1 className="mt-3 text-2xl font-black text-background-50 md:text-4xl">{game.name}</h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-background-100 md:text-base">{game.summary}</p>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => openPlay(game)}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-bold text-background-50 transition hover:bg-primary-600"
            >
              <i className="ri-play-circle-fill text-lg"></i>
              在线试玩
            </button>
            <div className="flex items-center gap-4 rounded-md border border-background-50/25 bg-background-50/10 px-4 py-2.5 text-background-50 backdrop-blur">
              <span className="flex items-center gap-1.5 text-sm">
                <i className="ri-star-fill text-primary-400"></i>
                <strong>{game.rating.toFixed(1)}</strong> 分
              </span>
              <span className="h-4 w-px bg-background-50/30"></span>
              <span className="flex items-center gap-1.5 text-sm">
                <i className="ri-fire-fill text-primary-400"></i>
                {formatPlayers(game.players)}
              </span>
            </div>
          </div>
        </div>
      </section>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
            <h2 className="flex items-center gap-2 text-lg font-black text-foreground-900">
              <i className="ri-file-text-line text-primary-500"></i>
              游戏简介
            </h2>
            <p className="mt-4 text-sm leading-7 text-foreground-700 md:text-base">{game.description}</p>

            <h3 className="mt-6 text-base font-bold text-foreground-900">游戏特色</h3>
            <ul className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {game.features.map((feature) => (
                <li key={feature} className="flex items-start gap-2.5 rounded-md bg-background-100 px-3.5 py-3">
                  <i className="ri-checkbox-circle-fill mt-0.5 text-secondary-500"></i>
                  <span className="text-sm text-foreground-700">{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
            <h2 className="flex items-center gap-2 text-lg font-black text-foreground-900">
              <i className="ri-image-2-line text-primary-500"></i>
              游戏画面
            </h2>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {game.screenshots.map((shot, index) => (
                <div key={shot} className="relative overflow-hidden rounded-lg bg-background-200">
                  <img
                    src={shot}
                    alt={`${game.name} 游戏画面 ${index + 1}`}
                    title={`${game.name} 截图 ${index + 1}`}
                    className="h-48 w-full object-cover object-top transition duration-500 hover:scale-105"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <aside className="space-y-6">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <div className="overflow-hidden rounded-lg bg-background-200">
              <img
                src={game.cover}
                alt={`${game.name} 封面`}
                title={`${game.name} 网页游戏封面`}
                className="h-56 w-full object-cover object-top"
              />
            </div>
            <button
              type="button"
              onClick={() => openPlay(game)}
              className="mt-4 inline-flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-sm font-bold text-background-50 transition hover:bg-primary-600"
            >
              <i className="ri-play-fill text-lg"></i>
              立即在线试玩
            </button>
            <p className="mt-2 text-center text-xs text-foreground-400">免下载安装 · 前端演示试玩窗口</p>

            <dl className="mt-5 divide-y divide-background-200 border-t border-background-200 text-sm">
              <div className="flex items-center justify-between py-2.5">
                <dt className="text-foreground-500">游戏类型</dt>
                <dd className="font-medium text-foreground-900">{category?.name}</dd>
              </div>
              <div className="flex items-center justify-between py-2.5">
                <dt className="text-foreground-500">游戏题材</dt>
                <dd className="font-medium text-foreground-900">{game.theme}</dd>
              </div>
              <div className="flex items-center justify-between py-2.5">
                <dt className="text-foreground-500">发布时间</dt>
                <dd className="font-medium text-foreground-900">{formatDate(game.releaseDate)}</dd>
              </div>
              <div className="flex items-center justify-between py-2.5">
                <dt className="text-foreground-500">开发商</dt>
                <dd className="font-medium text-foreground-900">{game.developer}</dd>
              </div>
              <div className="flex items-center justify-between py-2.5">
                <dt className="text-foreground-500">玩家评级</dt>
                <dd className="flex items-center gap-1 font-medium text-foreground-900">
                  <i className="ri-star-fill text-primary-500"></i>
                  {game.rating.toFixed(1)} / 10
                </dd>
              </div>
            </dl>

            <div className="mt-5 border-t border-background-200 pt-4">
              <p className="mb-2.5 text-xs font-bold uppercase tracking-wide text-foreground-400">游戏标签</p>
              <div className="flex flex-wrap gap-2">
                {game.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-900 transition hover:bg-secondary-200"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </div>

            <div className="mt-5 border-t border-background-200 pt-4">
              <ShareBar game={game} />
            </div>
          </div>

          <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
            <h3 className="flex items-center gap-2 text-sm font-bold text-accent-900">
              <i className="ri-information-line"></i>
              试玩说明
            </h3>
            <p className="mt-2 text-xs leading-relaxed text-accent-900/80">
              点击“在线试玩”会打开模拟启动窗口，用于演示试玩流程。该窗口为前端演示，暂不支持真实游戏运行，
              后续接入真实游戏资源后即可直接游玩。
            </p>
          </div>
        </aside>
      </div>

      {relatedGames.length > 0 && (
        <section className="mt-14">
          <SectionHeader
            eyebrow="猜你喜欢"
            title="相关推荐游戏"
            subtitle="根据游戏类型与标签为你匹配的同类型热门游戏。"
            moreTo={`/category/${game.category}`}
            moreLabel={`更多${category?.name ?? "同类型"}游戏`}
          />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {relatedGames.map((item) => (
              <GameCard key={item.id} game={item} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}